import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Facebook, X, Instagram, Mail, Hash } from 'lucide-react';
import NewsletterModal from './NewsletterModal';

const Footer = ({ openNewsletter }) => {
  const currentYear = new Date().getFullYear();
  const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const closeNewsletter = () => setIsNewsletterOpen(false);

  const footerLinks = {
    Company: [
      { label: 'About ANIMAC', path: '/about' },
      { label: 'Contact', path: 'https://forms.gle/FHkorLKUmybNVu5j8' },
      { label: 'Careers', path: '/careers' },
      { label: 'Press Kit', path: '/press-kit' },
    ],
    Content: [
      { label: 'OUTRANGE Hub', path: '/outrange' },
      { label: 'EAST Portal', path: '/outrange/east' },
      { label: 'WEST Portal', path: '/outrange/west' },
      { label: 'Streamfinite', path: '/streamfinite' },
    ],
    Community: [
      { label: 'Community Hub', path: '/community' },
      { label: 'Community Calendar', path: '/community/calendar' },
      { label: 'Community Events', path: '/community' },
    ],
    Policies: [
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Terms of Service', path: '/terms' },
      { label: 'Cookie Policy', path: '/cookies' },
      { label: 'DMCA', path: '/dmca' },
    ]
  };

  const popularTags = [
    'Anime', 'Manga', 'Movies', 'TV Shows', 'Cartoons', 'Reviews', 'News', 
    'Analysis', 'Opinion', 'Lists', 'Rankings', 'Spotlight', 'Trending', 
    'Streaming', 'Netflix', 'Disney', 'Studio Ghibli'
  ];

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://animac-metaverse.onrender.com';
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'footer' })
      });
      if (!res.ok) throw new Error('Subscribe failed');
      setSubmitted(true);
      setEmail('');
      setTimeout(() => setSubmitted(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .footer-root {
          background: linear-gradient(to top, #08080c 0%, #0c0c12 100%);
          color: #fff;
          font-family: -apple-system, 'Helvetica Neue', sans-serif;
          position: relative;
          overflow: hidden;
        }
        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 64px clamp(16px,4vw,48px) 48px;
          position: relative;
          z-index: 1;
        }
        .footer-grid {
          display: grid;
          grid-template-columns: 1.5fr repeat(4, 1fr);
          gap: 32px;
        }
        @media(max-width: 1024px) {
          .footer-grid { grid-template-columns: repeat(3, 1fr); }
          .footer-brand { grid-column: 1 / -1; }
        }
        @media(max-width: 680px) {
          .footer-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media(max-width: 480px) {
          .footer-grid { grid-template-columns: 1fr; }
        }
        .footer-brand-logo {
          font-family: 'Azonix', 'Impact', sans-serif;
          font-size: 28px;
          letter-spacing: 1px;
          margin-bottom: 8px;
        }
        .footer-brand-tagline {
          font-size: 11px;
          color: rgba(255,255,255,0.28);
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 16px;
        }
        .footer-brand-desc {
          font-size: 13px;
          color: rgba(255,255,255,0.42);
          line-height: 1.7;
          max-width: 280px;
          font-family: 'Georgia', serif;
          margin-bottom: 20px;
        }
        .footer-social {
          display: flex;
          gap: 10px;
        }
        .footer-social-link {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.42);
          text-decoration: none;
          transition: all 0.25s ease;
        }
        .footer-social-link:hover {
          background: rgba(255,255,255,0.1);
          color: #fff;
          transform: translateY(-2px);
        }
        .footer-col-title {
          font-family: 'Azonix', 'Impact', sans-serif;
          font-size: 10px;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.28);
          margin-bottom: 18px;
        }
        .footer-links {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .footer-links li {
          margin-bottom: 10px;
        }
        .footer-link {
          font-size: 13px;
          color: rgba(255,255,255,0.5);
          text-decoration: none;
          transition: color 0.2s ease;
          display: inline-block;
        }
        .footer-link:hover {
          color: #fff;
        }
        .footer-divider {
          height: 1px;
          background: linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent);
          margin: 48px 0 32px;
        }
        .footer-tags {
          margin-bottom: 32px;
        }
        .footer-tags-title {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }
        .footer-tags-icon {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          background: linear-gradient(135deg, rgba(167,139,250,0.15), rgba(6,182,212,0.1));
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .footer-tags-label {
          font-family: 'Azonix', 'Impact', sans-serif;
          font-size: 12px;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.5);
        }
        .footer-tags-cloud {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .footer-tag {
          padding: 6px 14px;
          border-radius: 100px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          font-size: 11px;
          color: rgba(255,255,255,0.4);
          transition: all 0.2s ease;
          cursor: pointer;
        }
        .footer-tag:hover {
          background: rgba(255,255,255,0.1);
          color: #fff;
          border-color: rgba(255,255,255,0.15);
        }
        .footer-newsletter {
          text-align: center;
          padding: 40px;
          border-radius: 16px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.06);
          position: relative;
          overflow: hidden;
        }
        .footer-newsletter-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 300px;
          height: 150px;
          border-radius: 50%;
          background: radial-gradient(ellipse, rgba(167,139,250,0.08), transparent);
          filter: blur(40px);
          pointer-events: none;
        }
        .footer-newsletter-title {
          font-family: 'Azonix', 'Impact', sans-serif;
          font-size: 18px;
          margin-bottom: 8px;
          position: relative;
          z-index: 1;
        }
        .footer-newsletter-desc {
          font-size: 13px;
          color: rgba(255,255,255,0.38);
          margin-bottom: 20px;
          position: relative;
          z-index: 1;
          font-family: 'Georgia', serif;
        }
        .footer-newsletter-form {
          display: flex;
          gap: 10px;
          max-width: 380px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }
        .footer-newsletter-input {
          flex: 1;
          padding: 12px 16px;
          border-radius: 10px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          color: #fff;
          font-size: 13px;
          outline: none;
          transition: border-color 0.2s;
        }
        .footer-newsletter-input::placeholder {
          color: rgba(255,255,255,0.22);
        }
        .footer-newsletter-input:focus {
          border-color: rgba(255,255,255,0.2);
        }
        .footer-newsletter-btn {
          padding: 12px 24px;
          border-radius: 10px;
          background: linear-gradient(135deg, #a78bfa, #8b5cf6);
          border: none;
          color: #fff;
          font-family: 'Azonix', 'Impact', sans-serif;
          font-size: 10px;
          letter-spacing: 1px;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.25s ease;
          white-space: nowrap;
        }
        .footer-newsletter-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(167,139,250,0.3);
        }
        .footer-newsletter-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .footer-newsletter-success {
          color: #4ade80;
          font-size: 12px;
          margin-top: 12px;
          position: relative;
          z-index: 1;
        }

        /* Mobile Responsive */
        @media (max-width: 640px) {
          .footer-newsletter {
            padding: 24px 16px;
          }
          .footer-newsletter-form {
            flex-direction: column;
            gap: 10px;
          }
          .footer-newsletter-btn {
            width: 100%;
          }
        }
        .footer-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 32px;
          flex-wrap: wrap;
          gap: 16px;
        }
        .footer-bottom-copy {
          font-size: 11px;
          color: rgba(255,255,255,0.28);
        }
        .footer-bottom-links {
          display: flex;
          gap: 20px;
          font-size: 11px;
          color: rgba(255,255,255,0.28);
        }
        .footer-bottom-link {
          color: rgba(255,255,255,0.28);
          text-decoration: none;
          transition: color 0.2s;
        }
        .footer-bottom-link:hover {
          color: #fff;
        }
      `}</style>

      <footer className="footer-root">
        <div className="footer-container">
          {/* Main Footer Grid */}
          <div className="footer-grid">
            {/* Brand Section */}
            <div className="footer-brand">
              <div className="footer-brand-logo">ANIMAC</div>
              <div className="footer-brand-tagline">Where Culture Meets Commentary</div>
              <p className="footer-brand-desc">
                Your destination for anime, movies, and western cartoons. Discover stories that shape our streaming culture.
              </p>
              <div className="footer-social">
                <a href="https://x.com/Animac_Official?t=TVShBCQ6mbL-85Q5TFv02A&s=09" className="footer-social-link" aria-label="X">
                  <X size={14} />
                </a>
                <a href="https://www.instagram.com/animacbuzzfeed?igsh=b2d6aHdlN2RnMXBw" className="footer-social-link" aria-label="Instagram">
                  <Instagram size={14} />
                </a>
                <a href="https://www.facebook.com/share/16J2CHYuEW/" className="footer-social-link" aria-label="Facebook">
                  <Facebook size={14} />
                </a>
                <a href="mailto:iconanimac@gmail.com" className="footer-social-link" aria-label="Email">
                  <Mail size={14} />
                </a>
              </div>
            </div>

            {/* Link Columns */}
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h3 className="footer-col-title">{category}</h3>
                <ul className="footer-links">
                  {links.map((link, i) => (
                    <li key={i}>
                      <Link to={link.path} className="footer-link">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="footer-divider" />

          {/* Tag Cloud */}
          <div className="footer-tags">
            <div className="footer-tags-title">
              <div className="footer-tags-icon">
                <Hash size={14} color="#a78bfa" />
              </div>
              <span className="footer-tags-label">Most Discussed</span>
            </div>
            <div className="footer-tags-cloud">
              {popularTags.map((tag, i) => (
                <motion.span
                  key={tag}
                  className="footer-tag"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.03 }}
                  whileHover={{ scale: 1.05 }}
                >
                  #{tag}
                </motion.span>
              ))}
            </div>
          </div>

          {/* Newsletter */}
          <motion.div
            className="footer-newsletter"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="footer-newsletter-glow" />
            <h3 className="footer-newsletter-title">Stay in the Loop</h3>
            <p className="footer-newsletter-desc">
              Weekly updates on anime releases, movie reviews, and cartoon spotlights.
            </p>
            <form className="footer-newsletter-form" onSubmit={handleSubscribe}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="footer-newsletter-input"
                required
              />
              <button type="submit" className="footer-newsletter-btn" disabled={submitting}>
                {submitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {submitted && <div className="footer-newsletter-success">Thanks! Check your inbox.</div>}
          </motion.div>

          {/* Bottom Bar */}
          <div className="footer-bottom">
            <div className="footer-bottom-copy">
              © {currentYear} ANIMAC. All rights reserved. | Streaming Culture. Streaming Stories.
            </div>
            <div className="footer-bottom-links">
              <a href="/privacy" className="footer-bottom-link">Privacy</a>
              <a href="https://www.termsfeed.com/live/078404e4-889d-453b-ac6c-fc3458d9394c" className="footer-bottom-link" target="_blank" rel="noopener noreferrer">Terms</a>
              <a href="/cookies" className="footer-bottom-link">Cookies</a>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Made with <span style={{ color: '#dc2626' }}>❤</span> for the community
              </span>
            </div>
          </div>
        </div>

        {/* Newsletter Modal */}
        {isNewsletterOpen && <NewsletterModal isOpen={isNewsletterOpen} onClose={closeNewsletter} />}
      </footer>
    </>
  );
};

export default Footer;
